import { ComponentConfig } from "@measured/puck";
import { Autocomplete, Box, FormControl, Stack, TextField, Typography, useTheme } from "@mui/material";
import { Button } from "@foodpilot/foods";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useEffect, useMemo, useState } from "react";
import { t } from "i18next";

import { PUCK_CONFIG } from "../../constants";
import { withContexts } from "../../wrappers/withContexts";
import NativePuckTitleField from "../../fields/NativePuckTitleField";
import RichTextEditorField from "../../fields/RichTextEditorField";
import type { RichTextEditorFieldProps } from "../../fields/RichTextEditorField";

import SsqAnswers, { SsqAnswersFieldConfig, SsqAnswersProps } from "./SsqAnswers";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

type FieldsEditorProps = {
    value?: SsqAnswersFieldConfig[] | undefined;
    onChange?: (value: SsqAnswersFieldConfig[] | undefined) => void;
    readOnly?: boolean;
};

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

const parseFields = (v: unknown): SsqAnswersFieldConfig[] => {
    if (!Array.isArray(v)) return [];
    return v.flatMap((item) => {
        if (!isRecord(item)) return [];
        const id = Number(item.ssqPropertyId);
        if (!Number.isFinite(id) || id <= 0) return [];
        return [
            {
                ssqPropertyId: id,
                title: typeof item.title === "string" ? item.title : undefined,
                hint: typeof item.hint === "string" ? item.hint : undefined,
            },
        ];
    });
};

const FieldsEditorBase = (props: FieldsEditorProps) => {
    const { value, onChange, readOnly } = props;
    const theme = useTheme();
    const { selectedCompany, currentCampaign } = useFoodpilotContext();

    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : undefined;
    const campaignId = currentCampaign?.id ? Number(currentCampaign.id) : undefined;
    const ssqId = selectedCompany?.organizationSector?.primarySsqId || selectedCompany?.organizationSector?.ssqId;

    const { post: companyPost } = useGetOrganizationPost({
        companyId,
        ssqId,
        campaignId,
        enabled: Boolean(companyId && ssqId && campaignId),
    });

    const availableProperties = useMemo(() => {
        const elements = companyPost?.elements ?? [];
        const map = new Map<number, string>();
        elements.forEach((el) => {
            const id = el?.ssqPropertyId;
            if (typeof id !== "number") return;
            const label = typeof el?.name === "string" && el.name.trim() !== "" ? el.name : `#${id}`;
            if (!map.has(id)) {
                map.set(id, label);
            }
        });

        return Array.from(map.entries())
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [companyPost?.elements]);

    const [fields, setFields] = useState<SsqAnswersFieldConfig[]>(() => parseFields(value));

    useEffect(() => {
        setFields(parseFields(value));
    }, [value]);

    const puckInputStyles = {
        fontFamily: PUCK_CONFIG.FontFamily,
        fontSize: "0.875rem",
        "& .MuiOutlinedInput-notchedOutline": {
            border: `1px solid ${theme.custom.grey[700]} !important`,
        },
    };

    const commit = (next: SsqAnswersFieldConfig[]) => {
        setFields(next);
        onChange?.(next);
    };

    const addField = () => {
        const fallbackId = availableProperties[0]?.id ?? 0;
        commit([...(fields ?? []), { ssqPropertyId: fallbackId }]);
    };

    const removeField = (idx: number) => {
        commit(fields.filter((_, i) => i !== idx));
    };

    const updateField = (idx: number, patch: Partial<SsqAnswersFieldConfig>) => {
        const next = fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
        commit(next);
    };

    const PROPERTY_PAGE_SIZE = 50;
    const [propertySearch, setPropertySearch] = useState("");
    const [propertyLimit, setPropertyLimit] = useState(PROPERTY_PAGE_SIZE);

    const filteredProperties = useMemo(() => {
        const q = propertySearch.trim().toLowerCase();
        if (q === "") return availableProperties;
        return availableProperties.filter((p) => p.label.toLowerCase().includes(q) || String(p.id).includes(q));
    }, [availableProperties, propertySearch]);

    const visibleProperties = useMemo(() => {
        return filteredProperties.slice(0, propertyLimit);
    }, [filteredProperties, propertyLimit]);

    return (
        <Stack spacing={theme.spacing(2)}>
            <NativePuckTitleField label={t("puck.blocks.foods_form.fields", "Fields")} icon="edit" />

            {fields.map((field, idx) => {
                const selectedProperty = availableProperties.find((p) => p.id === field.ssqPropertyId) ?? null;
                return (
                    <Box
                        key={`${field.ssqPropertyId}-${idx}`}
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: theme.spacing(1),
                            padding: theme.spacing(1.5),
                            display: "flex",
                            flexDirection: "column",
                            gap: theme.spacing(1.5),
                        }}
                    >
                        <Stack direction="row" gap={theme.spacing(1)} alignItems="flex-start">
                            <FormControl fullWidth>
                                <Typography
                                    variant="caption"
                                    sx={{ fontFamily: PUCK_CONFIG.FontFamily, color: theme.palette.text.secondary }}
                                >
                                    {t("puck.blocks.foods_form.property", "Property")}
                                </Typography>

                                <Autocomplete
                                    disablePortal
                                    options={visibleProperties}
                                    value={selectedProperty}
                                    getOptionLabel={(o) => `${o.label} (#${o.id})`}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    onChange={(_, next) => {
                                        updateField(idx, { ssqPropertyId: next?.id ?? 0 });
                                    }}
                                    onInputChange={(_, newValue) => {
                                        setPropertySearch(newValue);
                                        setPropertyLimit(PROPERTY_PAGE_SIZE);
                                    }}
                                    disabled={Boolean(readOnly)}
                                    filterOptions={(x) => x}
                                    renderOption={(liProps, option) => (
                                        <li
                                            {...liProps}
                                            style={{
                                                ...liProps.style,
                                                fontFamily: "var(--puck-font-family)",
                                                fontSize: "0.875rem",
                                                padding: `${theme.spacing(0.75)} ${theme.spacing(2)}`,
                                            }}
                                        >
                                            {`${option.label} (#${option.id})`}
                                        </li>
                                    )}
                                    ListboxProps={{
                                        style: {
                                            maxHeight: 240,
                                            overflow: "auto",
                                        },
                                        onScroll: (e) => {
                                            const el = e.currentTarget as HTMLElement;
                                            const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
                                            if (!nearBottom) return;
                                            setPropertyLimit((prev) => {
                                                const next = prev + PROPERTY_PAGE_SIZE;
                                                return next > filteredProperties.length ?
                                                        filteredProperties.length
                                                    :   next;
                                            });
                                        },
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            margin="dense"
                                            placeholder={t("common.search", "Search")}
                                            sx={{
                                                ...puckInputStyles,
                                                "& .MuiInputBase-root": {
                                                    ...puckInputStyles,
                                                    padding: theme.spacing(0.5, 1.5),
                                                    paddingRight: theme.spacing(4),
                                                },
                                                "& .MuiAutocomplete-input": {
                                                    fontFamily: PUCK_CONFIG.FontFamily,
                                                    fontSize: "0.875rem",
                                                    padding: theme.spacing(1, 0.5),
                                                },
                                            }}
                                        />
                                    )}
                                    sx={{
                                        mt: 0,
                                        ...puckInputStyles,
                                        "& .MuiAutocomplete-endAdornment": {
                                            right: theme.spacing(1),
                                        },
                                    }}
                                />
                            </FormControl>
                        </Stack>

                        {!readOnly && (
                            <Button
                                fullWidth
                                variant="ternary"
                                onClick={() => removeField(idx)}
                                startIcon={<DeleteOutlineIcon fontSize="small" />}
                                sx={{
                                    fontFamily: PUCK_CONFIG.FontFamily,
                                    justifyContent: "center",
                                    color: "error.main",
                                }}
                            >
                                {t("puck.blocks.foods_form.delete_property", "Delete property")}
                            </Button>
                        )}

                        <RichTextEditorField
                            label={t("common.title", "Title")}
                            value={field.title ?? ""}
                            onChange={(v) => updateField(idx, { title: v })}
                            readOnly={Boolean(readOnly)}
                            minHeight={64}
                            maxHeight={180}
                        />

                        <RichTextEditorField
                            label={t("common.hint", "Hint")}
                            value={field.hint ?? ""}
                            onChange={(v) => updateField(idx, { hint: v })}
                            readOnly={Boolean(readOnly)}
                            minHeight={96}
                            maxHeight={240}
                        />
                    </Box>
                );
            })}

            {!readOnly && (
                <Box>
                    <Button
                        fullWidth
                        variant="primary"
                        onClick={addField}
                        startIcon={<AddIcon fontSize="small" />}
                        sx={{ fontFamily: PUCK_CONFIG.FontFamily, justifyContent: "center" }}
                    >
                        {t("puck.blocks.foods_form.add_field", "Add field")}
                    </Button>
                </Box>
            )}
        </Stack>
    );
};

const FieldsEditor = withContexts(FieldsEditorBase);

export type SsqAnswersConfigProps = SsqAnswersProps;

export const SsqAnswersConfig: ComponentConfig<SsqAnswersConfigProps> = {
    label: "SSQ Answers",
    fields: {
        title: {
            type: "custom",
            label: "Title",
            render: (props: RichTextEditorFieldProps) => (
                <RichTextEditorField {...props} label={t("common.title", "Title")} minHeight={64} maxHeight={180} />
            ),
        },
        fields: {
            type: "custom",
            label: "Fields",
            render: (props) => <FieldsEditor {...props} />,
        },
    },
    defaultProps: {
        title: "",
        fields: [],
    },
    render: (props: SsqAnswersConfigProps) => {
        return <SsqAnswers {...props} />;
    },
};

export default SsqAnswersConfig;
